import React, { Component } from 'react';
import { Card, CardTitle, CardText, CardActions } from 'material-ui/Card';
import TextField from 'material-ui/TextField';
import RaisedButton from 'material-ui/RaisedButton';
import FlatButton from 'material-ui/FlatButton';
import CircularProgress from 'material-ui/CircularProgress';
import Snackbar from 'material-ui/Snackbar';
import { API_BASE_URL } from '../endpoints';

const ENDPOINT = `${API_BASE_URL}/admin/silvasoft_settings`;

const styles = {
    page:        { padding: '1.5em', maxWidth: 720 },
    field:       { width: '100%' },
    actions:     { display: 'flex', gap: '0.5em', marginTop: '1em' },
    statusOk:    { color: '#2e7d32', marginTop: '0.5em' },
    statusFail:  { color: '#c62828', marginTop: '0.5em' },
    helper:      { fontSize: 12, color: '#666', marginTop: 4 },
};

const authHeaders = () => {
    const token = localStorage.getItem('token');
    return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
    };
};

class SilvasoftSettings extends Component {
    state = {
        loading: true,
        saving: false,
        testing: false,
        username: '',
        apiKey: '',
        apiKeyPlaceholder: '',
        apiKeyPresent: false,
        baseUrl: 'https://rest-api.silvasoft.nl',
        productNumber: '',
        dueDays: 14,
        taxCodeReverseCharge: '',
        snackbar: { open: false, msg: '' },
        testResult: null, // {ok: bool, message: string}
    };

    componentDidMount() {
        this.load();
    }

    load = () => {
        this.setState({ loading: true });
        fetch(ENDPOINT, { headers: authHeaders() })
            .then((r) => {
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                return r.json();
            })
            .then((data) => {
                this.setState({
                    loading: false,
                    username: data.username || '',
                    apiKey: '',
                    apiKeyPlaceholder: data.api_key_masked || '',
                    apiKeyPresent: !!data.api_key_present,
                    baseUrl: data.base_url || 'https://rest-api.silvasoft.nl',
                    productNumber: data.default_product_number || '',
                    dueDays: data.default_invoice_due_days != null ? data.default_invoice_due_days : 14,
                    taxCodeReverseCharge: data.tax_code_reverse_charge || '',
                });
            })
            .catch((err) => {
                this.setState({ loading: false, snackbar: { open: true, msg: `Laden mislukt: ${err.message}` } });
            });
    };

    onChange = (field) => (_e, value) => this.setState({ [field]: value, testResult: null });

    save = () => {
        const { username, apiKey, baseUrl, productNumber, dueDays, taxCodeReverseCharge } = this.state;
        const body = {
            username,
            base_url: baseUrl,
            default_product_number: productNumber,
            default_invoice_due_days: dueDays,
            tax_code_reverse_charge: taxCodeReverseCharge,
        };
        if (apiKey) body.api_key = apiKey;

        this.setState({ saving: true });
        fetch(ENDPOINT, {
            method: 'PUT',
            headers: authHeaders(),
            body: JSON.stringify(body),
        })
            .then((r) => r.json().then((j) => ({ ok: r.ok, status: r.status, body: j })))
            .then(({ ok, body }) => {
                if (!ok) throw new Error(body.error || body.exception || 'Onbekende fout');
                this.setState({
                    saving: false,
                    apiKey: '',
                    apiKeyPlaceholder: body.api_key_masked || '',
                    apiKeyPresent: !!body.api_key_present,
                    snackbar: { open: true, msg: 'Opgeslagen' },
                });
            })
            .catch((err) => {
                this.setState({ saving: false, snackbar: { open: true, msg: `Opslaan mislukt: ${err.message}` } });
            });
    };

    test = () => {
        const { username, apiKey, baseUrl } = this.state;
        const body = {};
        if (username) body.username = username;
        if (apiKey) body.api_key = apiKey;
        if (baseUrl) body.base_url = baseUrl;

        this.setState({ testing: true, testResult: null });
        fetch(`${ENDPOINT}/test`, {
            method: 'POST',
            headers: authHeaders(),
            body: JSON.stringify(body),
        })
            .then((r) => r.json().then((j) => ({ ok: r.ok, body: j })))
            .then(({ body }) => {
                this.setState({
                    testing: false,
                    testResult: { ok: !!body.ok, message: body.ok ? body.message : `${body.error || 'Verbinding mislukt'} (status ${body.status})` },
                });
            })
            .catch((err) => {
                this.setState({ testing: false, testResult: { ok: false, message: err.message } });
            });
    };

    render() {
        const { loading, saving, testing, username, apiKey, apiKeyPlaceholder, apiKeyPresent, baseUrl, snackbar, testResult } = this.state;

        return (
            <div style={styles.page}>
                <Card>
                    <CardTitle title="Silvasoft koppeling" subtitle="API-credentials voor het versturen van facturen via Silvasoft." />
                    {loading ? (
                        <CardText><CircularProgress size={28} /></CardText>
                    ) : (
                        <div>
                            <CardText>
                                <TextField
                                    style={styles.field}
                                    floatingLabelText="Silvasoft username"
                                    value={username}
                                    onChange={this.onChange('username')}
                                />

                                <TextField
                                    style={styles.field}
                                    floatingLabelText={apiKeyPresent ? `API key (huidig: ${apiKeyPlaceholder} — laat leeg om te behouden)` : 'API key'}
                                    value={apiKey}
                                    type="password"
                                    onChange={this.onChange('apiKey')}
                                />
                                <div style={styles.helper}>
                                    Genereer in Silvasoft → Instellingen → API. Wordt versleuteld opgeslagen.
                                </div>

                                <TextField
                                    style={styles.field}
                                    floatingLabelText="Base URL"
                                    value={baseUrl}
                                    onChange={this.onChange('baseUrl')}
                                />

                                <TextField
                                    style={styles.field}
                                    floatingLabelText="ProductNumber (artikelnummer in Silvasoft)"
                                    hintText="bv. 6"
                                    value={this.state.productNumber}
                                    onChange={this.onChange('productNumber')}
                                />
                                <div style={styles.helper}>
                                    Het Silvasoft-artikel dat als regel op elke factuur komt (bv. "Adverteren").
                                </div>

                                <TextField
                                    style={styles.field}
                                    floatingLabelText="Standaard betaaltermijn (dagen)"
                                    type="number"
                                    value={this.state.dueDays}
                                    onChange={(_e, v) => this.setState({ dueDays: parseInt(v, 10) || 0, testResult: null })}
                                />

                                <TextField
                                    style={styles.field}
                                    floatingLabelText="TaxCode voor BTW verlegd (buitenland EU)"
                                    hintText="bv. 4 — kijk in Silvasoft welke string juist is"
                                    value={this.state.taxCodeReverseCharge}
                                    onChange={this.onChange('taxCodeReverseCharge')}
                                />
                                <div style={styles.helper}>
                                    Wordt als <code>TaxCode</code> meegestuurd voor niet-NL klanten met VAT (i.p.v. <code>TaxPc 0</code>). Werkt wanneer Silvasoft de exacte string accepteert. Eerdere foutmelding gaf "Tax percentage not found" — probeer eventueel ook andere notaties zoals <code>BTW04</code>, <code>VERLEGD</code>, of vraag in Silvasoft op welke string hoort bij "0% buitenland binnen EU". Leeg laten = TaxPc 0 wordt gebruikt (Silvasoft pakt dan code 1).
                                </div>

                                {testResult && (
                                    <div style={testResult.ok ? styles.statusOk : styles.statusFail}>
                                        {testResult.ok ? '✓ ' : '✗ '}{testResult.message}
                                    </div>
                                )}
                            </CardText>

                            <CardActions style={styles.actions}>
                                <RaisedButton
                                    label={saving ? 'Bezig...' : 'Opslaan'}
                                    primary
                                    disabled={saving || !username || (!apiKey && !apiKeyPresent)}
                                    onClick={this.save}
                                />
                                <FlatButton
                                    label={testing ? 'Testen...' : 'Test verbinding'}
                                    disabled={testing || (!username && !apiKeyPresent) || (!apiKey && !apiKeyPresent)}
                                    onClick={this.test}
                                />
                            </CardActions>
                        </div>
                    )}
                </Card>

                <Snackbar
                    open={snackbar.open}
                    message={snackbar.msg}
                    autoHideDuration={3500}
                    onRequestClose={() => this.setState({ snackbar: { open: false, msg: '' } })}
                />
            </div>
        );
    }
}

export default SilvasoftSettings;



// WEBPACK FOOTER //
// ./src/silvasoft/SilvasoftSettings.js